import { BrowserAuthError, InteractionRequiredAuthError, SilentRequest } from '@azure/msal-browser'
import axios, { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { msalInstance } from '../msalInstance'
import { backendUrl } from './apiConfig'

// Opt-out of the global error toasts for requests whose failures are handled
// inline by the caller (e.g. asset images render their own fallback). Errors
// are still thrown — only the toast is suppressed.
declare module 'axios' {
  export interface AxiosRequestConfig {
    suppressErrorToast?: boolean
  }
}

function expireSession() {
  localStorage.removeItem('jwt_token')
  localStorage.clear()
  sessionStorage.clear()

  document.cookie.split(';').forEach(function (c) {
    document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/')
  })

  window.location.href = '/'
}

const axiosClient = axios.create({
  baseURL: backendUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

export async function getAuthHeaders(): Promise<
  { Authorization: string; 'Content-Type': string } | Record<string, never>
> {
  const jwtToken = localStorage.getItem('jwt_token')
  if (jwtToken) {
    return {
      Authorization: `Bearer ${jwtToken}`,
      'Content-Type': 'application/json',
    }
  }
  if (!msalInstance) {
    console.warn('MSAL instance not initialized for Axios interceptor.')
    return {}
  }

  const accounts = msalInstance.getAllAccounts()
  if (accounts.length === 0) {
    console.warn('No active MSAL account found. Skipping token acquisition.')
    return {}
  }

  const request: SilentRequest = {
    scopes: ['user.read'],
    account: accounts[0],
    forceRefresh: false,
  }

  try {
    const response = await msalInstance.acquireTokenSilent(request)
    return {
      Authorization: `Bearer ${response.accessToken}`,
      'Content-Type': 'application/json',
    }
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      try {
        console.log('Interaction required, acquiring token redirect')
        await msalInstance.acquireTokenRedirect(request)
        return {}
      } catch (redirectError) {
        if (redirectError instanceof BrowserAuthError && redirectError.errorCode === 'interaction_in_progress') {
          console.warn('Another authentication is already in progress, skipping this request')
          return {}
        }
        throw redirectError
      }
    }
    console.error('Error acquiring token silently:', error)
    expireSession()
    throw error
  }
}

axiosClient.interceptors.request.use(
  async config => {
    try {
      const authHeaders = await getAuthHeaders()
      if (authHeaders && 'Authorization' in authHeaders) {
        config.headers.set('Authorization', authHeaders.Authorization)
      }
      return config
    } catch (error) {
      return Promise.reject(error)
    }
  },
  error => {
    return Promise.reject(error)
  },
)

axiosClient.interceptors.response.use(
  <T>(response: AxiosResponse<T>): T => {
    return response.data
  },
  error => {
    // Callers that render their own failure state (inline asset images) opt
    // out of the toasts; the thrown error still reaches their catch.
    const notify: typeof toast.error = error.config?.suppressErrorToast ? () => '' : toast.error

    if (!error.response) {
      notify('Network error. Please check your connection.', {
        toastId: 'network-error',
        autoClose: 5000,
      })
      throw new Error('Network error. Please check your connection.')
    }

    const { status, data } = error.response

    switch (status) {
      case 401:
        toast.error('Authentication required. Please log in again.', {
          toastId: 'auth-error-401',
        })
        expireSession()
        throw new Error('Authentication required. Please log in again.')

      case 403:
        notify('Access denied. You do not have permission to perform this action.', {
          toastId: 'access-denied-403',
        })
        throw new Error('Access denied. You do not have permission to perform this action.')

      case 404:
        notify('Resource not found.', {
          toastId: `not-found-404-${error.config?.url}`,
        })
        throw new Error('Resource not found.')

      case 429:
        notify('Rate limit exceeded. Please try again later.', {
          toastId: 'rate-limit-429',
        })
        throw new Error('Rate limit exceeded. Please try again later.')

      case 500:
        notify('Server error. Please try again later.', {
          toastId: 'server-error-500',
        })
        throw new Error('Server error. Please try again later.')

      default: {
        const message =
          data?.error || data?.message || data?.detail || error.message || `Request failed with status ${status}`
        notify(message, {
          toastId: `error-${status}-${error.config?.url}`,
        })
        throw new Error(message)
      }
    }
  },
)

export { axiosClient }
